import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/format";
import { CATALOG_CATEGORY_SHORT, unitShort } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const CATEGORY_ORDER = ["masse", "vare", "teneste", "ressurs"];

/** Enkel, rask fuzzy-match: alle ord i søket må finnast i namn/varenr/kategori/skildring. */
const matches = (item, words) => {
  const hay = `${item.name} ${item.sku || ""} ${CATALOG_CATEGORY_SHORT[item.category] || ""} ${item.description || ""}`.toLowerCase();
  return words.every((w) => hay.includes(w));
};

/** Rangering: treff i varenr/namn-start først, så namn, så skildring. */
const score = (item, words) => {
  if (!words.length) return 0;
  const name = item.name.toLowerCase();
  const sku = (item.sku || "").toLowerCase();
  let s = 0;
  words.forEach((w) => {
    if (sku === w || name.startsWith(w)) s += 40;
    else if (sku.startsWith(w)) s += 30;
    else if (name.split(/[\s,()/–-]+/).some((part) => part.startsWith(w))) s += 20;
    else if (name.includes(w) || sku.includes(w)) s += 10;
    else s += 1;
  });
  return s;
};

/**
 * Produktsøk: skriv namn eller varenummer, filtrer på kategori, Enter/klikk legg til.
 * agreements: { [catalog_item_id]: { unit_price } } for å vise avtalepris.
 * showPrices=false for brukarar som ikkje skal sjå pris. Medarbeidarar får aldri pris, uansett prop.
 */
export const ProductPicker = ({ catalog = [], onPick, agreements = {}, showPrices = true, exclude = [], placeholder = "Søk produkt, teneste eller varenummer …", autoFocus = false, categories = CATEGORY_ORDER, inline = false, testId = "product-picker" }) => {
  const { isAdmin } = useAuth();
  const canSeePrices = showPrices && isAdmin;
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef(null);

  const results = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return catalog
      .filter((c) => c.active !== false && categories.includes(c.category) && (!category || c.category === category) && !exclude.includes(c.id))
      .filter((c) => (words.length ? matches(c, words) : true))
      .sort((a, b) => score(b, words) - score(a, words) || CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category) || a.name.localeCompare(b.name, "nb"))
      .slice(0, 40);
  }, [catalog, query, category, exclude, categories]);

  useEffect(() => setActive(0), [query, category]);
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const pick = (item) => {
    onPick(item);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && ["ArrowDown", "Enter"].includes(e.key)) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[active]) pick(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const counts = useMemo(() => {
    const c = {};
    catalog.filter((x) => x.active !== false).forEach((x) => { c[x.category] = (c[x.category] || 0) + 1; });
    return c;
  }, [catalog]);

  return (
    <div className="relative" data-testid={testId}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          autoFocus={autoFocus}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          className="pl-9"
          data-testid={`${testId}-input`}
        />
      </div>
      {open ? (
        <div className={cn("overflow-hidden rounded-lg border border-border bg-popover", inline ? "mt-2" : "absolute left-0 right-0 top-full z-dropdown mt-1 shadow-sm")} data-testid={`${testId}-panel`}>
          <div className="scrollbar-none flex gap-1 overflow-x-auto border-b border-border px-2 py-1.5" role="tablist" aria-label="Kategori">
            {[null, ...categories].map((cat) => (
              <button
                key={cat || "alle"}
                type="button"
                role="tab"
                aria-selected={category === cat}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setCategory(cat)}
                className={cn("focus-ring inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-medium", category === cat ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted hover:text-foreground")}
                data-testid={`${testId}-cat-${cat || "alle"}`}
              >
                {cat ? CATALOG_CATEGORY_SHORT[cat] : "Alle"}
                <span className={cn("tabular text-[10px]", category === cat ? "text-brand-silver" : "text-muted-foreground")}>{cat ? counts[cat] || 0 : catalog.filter((x) => x.active !== false).length}</span>
              </button>
            ))}
          </div>
          <ul ref={listRef} role="listbox" className={cn("overflow-y-auto py-1", inline ? "max-h-56" : "max-h-72")} data-testid={`${testId}-listbox`}>
            {results.length === 0 ? <li className="px-3 py-3 text-sm text-muted-foreground">Ingen treff. Prøv eit anna ord eller varenummer.</li> : null}
            {results.map((item, i) => {
              const agreement = agreements[item.id];
              const price = agreement && agreement.unit_price !== null && agreement.unit_price !== undefined ? agreement.unit_price : item.unit_price;
              return (
                <li key={item.id} role="option" aria-selected={i === active} data-active={i === active}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(item)}
                    className={cn("flex w-full items-center gap-3 px-3 py-2 text-left", i === active && "bg-muted")}
                    data-testid={`${testId}-option`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{item.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {item.sku ? <span className="num">{item.sku} · </span> : null}{CATALOG_CATEGORY_SHORT[item.category]} · per {unitShort(item.unit)}
                      </span>
                    </span>
                    {canSeePrices ? (
                      <span className="shrink-0 text-right">
                        <span className={cn("num block text-sm", price === null || price === undefined ? "text-destructive" : "")}>{price === null || price === undefined ? "Pris manglar" : fmtMoney(price)}</span>
                        {agreement ? <span className="block text-[10px] font-medium text-info">Kundeavtale</span> : null}
                      </span>
                    ) : null}
                    <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
